// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useContext, useMemo} from 'react';
import {Editor} from '../../../shared/editor/editor';
import {Settings_context} from '../settings_ctx';
import {Button} from '../../../shared/button';
import {Install_btn} from '../ui/install_btn';
import {Agreement_message} from './agreement_message';
import {use_screen} from '../lib/screen_util';
import cn from 'classnames';
import styles from './get_started_banner.module.less';

function process_html_classes(html: string, process_class_name: (
class_name: string)=>string): string
{
  return html.replace(/class\s*=\s*["']([^"']+)["']/g, (match, list)=>{
    let new_list = list.split(/\s+/).map(process_class_name).join(' ');
    return `class="${new_list}"`;
  });
}

interface i_get_started_banner_props {
    title?: string;
    subtitle?: string;
    subtitle_width?: number;
    cta1?: string;
    cta1_show_arrow?: boolean;
    cta2?: string;
    cta2_show_button?: boolean;
    show_agreement?: boolean;
    disclaimer?: string;
    download_vpn?: boolean;
    app_fixed?: string;
    gameboost_href?: string;
}

const opt_props: i_get_started_banner_props = {
    title: 'Get Started with Hola VPN Today',
    subtitle: 'Install Hola VPN for free and enjoy worldwide content.',
    cta1: 'Get Hola Free VPN',
    cta1_show_arrow: false,
    cta2: 'See Premium',
    cta2_show_button: false,
    show_agreement: false,
    download_vpn: false,
    app_fixed: 'vpn',
};

export const Get_started_banner = (props: i_get_started_banner_props)=>{
    const {theme} = useContext(Settings_context);
    const {screen} = use_screen();
    const is_mobile = screen=='xs' || screen=='sm';
    const {title = opt_props.title, subtitle = opt_props.subtitle,
        cta1 = opt_props.cta1, cta2 = opt_props.cta2,
        cta1_show_arrow = opt_props.cta1_show_arrow,
        cta2_show_button = opt_props.cta2_show_button, subtitle_width,
        show_agreement = opt_props.show_agreement, disclaimer,
        download_vpn = opt_props.download_vpn,
        app_fixed = opt_props.app_fixed, gameboost_href} = props;
    const processed_title = useMemo(()=>
        process_html_classes(title || '', class_name=>
            theme[class_name] || class_name),
    [title, theme]);
    const processed_subtitle = useMemo(()=>
        process_html_classes(subtitle || '', class_name=>
            theme[class_name] || class_name),
    [subtitle, theme]);
    const processed_disclaimer = useMemo(()=>{
        if (!disclaimer)
            return null;
        return process_html_classes(disclaimer, class_name=>
            theme[class_name] || class_name);
    }, [disclaimer, theme]);
    const install_btn_cls = cn(['hola_btn',
        !is_mobile && 'hola_btn--lg',
        'hola_btn--round',
        'hola_btn--txt_colored hola_btn--no_jump',
        {
          'hola_btn--arrow': cta1_show_arrow,
          'hola_btn-no_arrow': !cta1_show_arrow
        },
        styles.button]);
      const subtitle_style =
          {'--subtitle_width': `${subtitle_width}px`} as React.CSSProperties;
    return <div className={styles.banner}>
        <div className={cn(theme.container, styles.content, {
          [styles.no_pd_btm]: !!processed_disclaimer,
        })}>
          <div className={styles.text}>
            <h2 className={cn(theme.H2, styles.title)}><span
              dangerouslySetInnerHTML={{__html: processed_title}}/></h2>
            <h3 className={cn(theme.H3, styles.title)}><span
              dangerouslySetInnerHTML={{__html: processed_title}}/></h3>
            <p className={cn(theme.u3, theme.med, styles.subtitle)}
              {...(subtitle_width && {style: subtitle_style})}>
              <span dangerouslySetInnerHTML={{__html: processed_subtitle}}/>
            </p>
            <div className={styles.buttons}>
              <Install_btn keep_btn_text text={cta1}
                className={install_btn_cls} app_fixed={app_fixed}
                no_redirect={app_fixed=='gameboost' ? !gameboost_href
                  : !download_vpn}
                download_vpn={download_vpn}
                gameboost_href={gameboost_href}/>
              {cta2_show_button && <Button cls={styles.button} caption={cta2}
                to="/premium" color="transparent"
                size={is_mobile ? 'md' : 'lg'}/>}
            </div>
            {!!show_agreement && <div className={styles.agreement_message}>
              <Agreement_message/></div>}
          </div>
        </div>
        {processed_disclaimer && <p className={cn(styles.disclaimer,
          theme.container)}><span
            dangerouslySetInnerHTML={{__html: processed_disclaimer}}/></p>}
        <div className={styles.gradient} aria-hidden="true" />
    </div>;
};

interface i_get_started_banner_editor_props {
    value: i_get_started_banner_props;
    on_change: (new_val: i_get_started_banner_props)=>void;
}

export const Get_started_banner_editor = (
{value, on_change}: i_get_started_banner_editor_props)=>{
    return <Editor value={value} on_change={on_change} editors={[
      {key: 'title', label: 'Title', type: 'text'},
      {key: 'subtitle', label: 'Subtitle', type: 'text'},
      {key: 'subtitle_width', label: 'Subtitle max width (px)',
        type: 'number'},
      {key: 'cta1', type: 'text', label: 'CTA 1'},
      {key: 'cta1_show_arrow', type: 'boolean', label: 'CTA 1 show arrow'},
      {key: 'cta2', type: 'text', label: 'CTA 2'},
      {key: 'cta2_show_button', type: 'boolean', label: 'CTA 2 show button'},
      {key: 'show_agreement', type: 'boolean',
        label: 'Show agreement message'},
      {key: 'disclaimer', type: 'textarea', label: 'Disclaimer'},
      {key: 'download_vpn', type: 'boolean', label: 'Download VPN'},
      {key: 'app_fixed', type: 'text',
        label: 'App type (vpn / gameboost / browser)'},
      {key: 'gameboost_href', type: 'text',
        label: 'Gameboost after-download URL (empty = no redirect)'},
    ]}/>;
};
